import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { useFormField } from '../FormField';
import './Input.css';

/**
 * Input — 손으로 짠 `<input>` **438회 / 132파일** 을 흡수한다 (02-components.md B2).
 *
 * ★★ **테두리·배경·초점 링은 «껍데기»(wrapper)가 그리고 `<input>` 은 투명하다.**
 *    앞뒤 슬롯(아이콘·단위·버튼)이 들어와도 상자는 하나로 보여야 하는데, 입력 자신이
 *    테두리를 그리면 슬롯이 상자 밖에 서거나 「슬롯 폭 + 8」을 매번 재야 한다.
 *    껍데기가 그리면 초점 링도 `:focus-within` 한 줄로 끝난다.
 *
 * ★ **높이는 F14 4px 격자만 쓴다** — sm 28 · md 32(기본) · lg 40.
 *   지금 화면들에 26·30·34 가 228회 흩어져 있는데, 옆에 선 것과 2px 씩 어긋난 그 값들이다.
 *
 * ★ **자리표시자를 라벨 대용으로 쓰지 않는다** — 값을 넣으면 사라져서, 나중에 이 칸이
 *   무엇이었는지 알 수 없다. 개발 중에는 라벨 없는 입력에 경고를 띄운다.
 *
 * FormField 안에 있으면 id · aria-describedby · aria-invalid · aria-required · disabled 를
 * **자동으로** 물려받는다. 호출부가 이을 것이 없다.
 */

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /** ⚠ 네이티브 `size`(글자 수) 는 안 받는다 — 이 이름은 높이 단이다. 폭은 CSS 로 준다. */
  size?: InputSize;
  /** 앞 슬롯 — 아이콘 · 단위. 눌러야 하는 것이면 suffix 쪽에 둔다. */
  prefix?: ReactNode;
  /** 뒤 슬롯 — 지우기 · 단위 · 작은 버튼. */
  suffix?: ReactNode;
  /** 오류 상태. 안 주면 FormField 의 판정을 따른다. */
  invalid?: boolean;
  /** 뒤 슬롯 자리에 스피너를 돌린다(suffix 는 잠시 가려진다). */
  loading?: boolean;
  fullWidth?: boolean;
  /** 껍데기(테두리를 그리는 상자)에 붙는 클래스. className 은 `<input>` 에 붙는다. */
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    prefix,
    suffix,
    invalid,
    loading = false,
    fullWidth = false,
    wrapperClassName,
    className,
    id,
    disabled,
    required,
    readOnly,
    type = 'text',
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const field = useFormField();

  const controlId = id ?? field?.controlId;
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const isRequired = required ?? field?.required ?? false;
  const describedBy = [ariaDescribedBy, field?.describedBy].filter(Boolean).join(' ') || undefined;

  if (import.meta.env.DEV && !field && !controlId && !rest['aria-label'] && !rest['aria-labelledby']) {
    // 라벨 없는 입력은 스크린리더에서 「편집 상자」로만 읽힌다. 자리표시자는 라벨이 아니다.
    console.warn('[Input] 라벨이 없다 — FormField 로 감싸거나 id/aria-label 을 준다.');
  }

  const wrapCls = [
    'wc-input',
    `wc-input--${size}`,
    isInvalid ? 'wc-input--invalid' : '',
    isDisabled ? 'wc-input--disabled' : '',
    readOnly ? 'wc-input--readonly' : '',
    fullWidth ? 'wc-input--full' : '',
    prefix != null ? 'wc-input--has-prefix' : '',
    suffix != null || loading ? 'wc-input--has-suffix' : '',
    wrapperClassName ?? '',
  ].filter(Boolean).join(' ');

  return (
    <span className={wrapCls}>
      {prefix != null && (
        <span className="wc-input__slot wc-input__slot--prefix" aria-hidden>{prefix}</span>
      )}
      <input
        ref={ref}
        id={controlId}
        type={type}
        className={['wc-input__el', className ?? ''].filter(Boolean).join(' ')}
        disabled={isDisabled}
        readOnly={readOnly}
        required={isRequired || undefined}
        aria-required={isRequired || undefined}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        aria-busy={loading || undefined}
        {...rest}
      />
      {(loading || suffix != null) && (
        <span className="wc-input__slot wc-input__slot--suffix">
          {loading ? <span className="wc-input__spinner" aria-hidden /> : suffix}
        </span>
      )}
    </span>
  );
});
